import React, { useState, useEffect, useMemo } from 'react';

// Rooms equipped with ultra-widescreen monitors
const ULTRAWIDE_ROOMS = ['O2', 'O3', 'C3', 'C6', 'C11', 'C14', 'C16', 'C20'];

// Default (SSP) map palette - used when an office config has no theme.map
const DEFAULT_MAP_THEME = {
  shared: { available: '#193d68', booked: '#5D5E60', hover: '#2a5a94', text: '#fff', bookedText: '#fff' },
  desk:   { available: '#47ACE4', booked: '#5D5E60', hover: '#6fc0ec', text: '#fff', bookedText: '#fff' },
  border: null,
  borderWidth: 2.5,
  unbookableFill: '#fff',
  unbookableText: '#333',
  unbookableSub: '#555',
  legend: [
    ['#193d68', 'Conference / Meeting / Huddle'],
    ['#47ACE4', 'Office / Cubicle'],
    ['#5D5E60', 'Booked'],
  ],
};

const SHARED_TYPES = ['conference', 'meeting', 'huddle'];

// Shrink font size when a label would touch the sides of its room
// (8 SVG units of padding each side)
function fitFont(text, w, base, scale = 1) {
  const b = base * scale;
  if (!text) return b;
  const max = (w - 16) / (0.58 * String(text).length);
  return Math.min(b, Math.max(9 * scale, Math.floor(max)));
}

const TYPE_LABELS = {
  conference: 'Conference Room',
  meeting: 'Meeting Room',
  huddle: 'Huddle Room',
  office: 'Office',
  cubicle: 'Cubicle',
};

/**
 * FloorPlan component - renders an interactive SVG map of any office.
 * All room data and layout coordinates come from the officeConfig prop,
 * so the same component works for every office location.
 */
export default function FloorPlan({ officeConfig, availability = {}, onRoomSelect, selectedRoom, isKiosk = false }) {
  const [hoveredRoom, setHoveredRoom] = useState(null);
  const [eggText, setEggText] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  const eggs = officeConfig.easterEggs || (officeConfig.easterEgg ? [officeConfig.easterEgg] : []);
  const triggerEgg = (text) => {
    if (eggText) return;
    setEggText(text);
    setTimeout(() => setEggText(null), 2800);
  };

  const rooms = officeConfig.rooms;
  const fixedAreas = officeConfig.fixedAreas || [];
  const mapTheme = officeConfig.theme?.map || DEFAULT_MAP_THEME;
  // Per-office label scale: floor-plan font sizes are tuned for MKE's viewBox.
  // Offices with a larger coordinate space set labelScale to keep labels legible.
  const labelScale = officeConfig.labelScale || 1;

  const getPalette = (room) =>
    SHARED_TYPES.includes(room.type) ? mapTheme.shared : mapTheme.desk;

  const getRoomColor = (room) => {
    const colors = getPalette(room);
    const isBooked = availability[room.id]?.currentlyBooked;
    if (isBooked) return colors.booked;
    if (hoveredRoom === room.id) return colors.hover;
    if (selectedRoom === room.id) return colors.hover;
    return colors.available;
  };

  const getRoomTextColor = (room) => {
    const colors = getPalette(room);
    const isBooked = availability[room.id]?.currentlyBooked;
    return isBooked ? (colors.bookedText || '#fff') : (colors.text || '#fff');
  };

  const hasFloorplanImage = !!officeConfig.floorplanImage;

  const getRoomOpacity = (room) => {
    const isBooked = availability[room.id]?.currentlyBooked;
    if (hasFloorplanImage) {
      // High opacity so rooms are clearly visible over the light gray floorplan
      if (hoveredRoom === room.id || selectedRoom === room.id) return 1;
      return 0.95;
    }
    if (isBooked) return 0.5;
    if (hoveredRoom === room.id || selectedRoom === room.id) return 0.9;
    return 0.7;
  };

  const availableCubicleCount = useMemo(() => {
    return rooms.filter(r => r.type === 'cubicle' && r.bookable !== false && !availability[r.id]?.currentlyBooked).length;
  }, [rooms, availability]);

  const availableOfficeCount = useMemo(() => {
    return rooms.filter(r => r.type === 'office' && r.bookable !== false && !availability[r.id]?.currentlyBooked).length;
  }, [rooms, availability]);

  // Determine which room types exist in this office for the legend
  const activeTypes = useMemo(() => {
    const types = new Set(rooms.map(r => r.type));
    return Object.keys(TYPE_LABELS).filter(t => types.has(t));
  }, [rooms]);

  return (
    <div className="floorplan-container">
      <div className="floorplan-header">
        <h2>{isKiosk ? (officeConfig.mapTitle || `${officeConfig.name} - Floor Plan`) : 'Book a Space'}</h2>
        <div className="legend">
          {(mapTheme.legend || DEFAULT_MAP_THEME.legend).map(([color, label]) => (
            <span className="legend-item" key={label}>
              <span className="legend-dot" style={{ backgroundColor: color }} />
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Quick-book buttons (only show if the types exist in this office) */}
      <div className="quick-book">
        {rooms.some(r => r.type === 'cubicle') && (
          <button
            className="quick-book-btn cubicle-btn"
            onClick={() => onRoomSelect?.('any-cubicle')}
            disabled={availableCubicleCount === 0}
          >
            Any Available Cubicle ({availableCubicleCount} open)
          </button>
        )}
        {rooms.some(r => r.type === 'office') && (
          <button
            className="quick-book-btn office-btn"
            onClick={() => onRoomSelect?.('any-office')}
            disabled={availableOfficeCount === 0}
          >
            Any Available Office ({availableOfficeCount} open)
          </button>
        )}
      </div>

      <div className={`floorplan-svg-wrap${zoomed ? ' zoomed' : ''}`}>
      <svg
        viewBox={officeConfig.svgViewBox}
        className="floorplan-svg"
        role="img"
        aria-label={`${officeConfig.name} office floor plan`}
      >
        {/* Light-gray architectural floor plan as background */}
        {officeConfig.floorplanImage && (
          <defs>
            <filter id="lighten-floorplan">
              <feColorMatrix type="saturate" values="0" />
              <feComponentTransfer>
                <feFuncR type="linear" slope="0.35" intercept="0.65" />
                <feFuncG type="linear" slope="0.35" intercept="0.65" />
                <feFuncB type="linear" slope="0.35" intercept="0.65" />
              </feComponentTransfer>
            </filter>
          </defs>
        )}
        {officeConfig.floorplanImage ? (
          <image
            href={officeConfig.floorplanImage}
            x="0" y="0"
            width={officeConfig.floorplanSize?.w || officeConfig.svgViewBox.split(' ')[2]}
            height={officeConfig.floorplanSize?.h || officeConfig.svgViewBox.split(' ')[3]}
            preserveAspectRatio="xMidYMid meet"
            filter="url(#lighten-floorplan)"
          />
        ) : (
          // Fallback: plain rectangle if no floorplan image
          (() => {
            const [, , vw, vh] = officeConfig.svgViewBox.split(' ').map(Number);
            return <rect x="0" y="0" width={vw} height={vh} fill="#f8f9fa" stroke="#333" strokeWidth="3" rx="4" />;
          })()
        )}

        {/* Fixed / non-bookable areas - only show if no floorplan image (already visible in the drawing) */}
        {!officeConfig.floorplanImage && fixedAreas.map((area) => (
          <g key={area.label}>
            <rect
              x={area.x} y={area.y} width={area.w} height={area.h}
              fill="#e0e0e0" stroke="#999" strokeWidth="1" rx="2"
            />
            <text
              x={area.x + area.w / 2} y={area.y + area.h / 2}
              textAnchor="middle" dominantBaseline="central"
              fontSize={11 * labelScale} fill="#666" fontWeight="500"
            >
              {area.label}
            </text>
          </g>
        ))}

        {/* Bookable rooms */}
        {rooms.filter(r => r.bookable !== false).map((room) => {
          const f = room.floor;
          const isBooked = availability[room.id]?.currentlyBooked;
          const isCubicle = room.type === 'cubicle';
          const fontSize = isCubicle ? 10 : 12;
          const isOffice = room.type === 'office';
          const isSharedRoom = room.type === 'conference' || room.type === 'meeting' || room.type === 'huddle';
          // Shared rooms (conference/meeting/huddle) stay clickable even when booked
          // because they support multiple bookings at different times
          const isLocked = isBooked && !isSharedRoom;
          const hasUltrawide = ULTRAWIDE_ROOMS.includes(room.id);
          // Small rooms (like focus booths): single centered label only
          const isSmall = f.h < 70;
          const baseLabel = isCubicle ? room.id
            : isOffice ? room.id
            : isSmall ? room.name.replace(/\s*Room\s*/i, ' ').trim()
            : room.name.split(' ')[0];
          const label = hasUltrawide ? `${baseLabel}*` : baseLabel;
          const sublabel = (isCubicle || isSmall) ? '' : room.type.charAt(0).toUpperCase() + room.type.slice(1);
          const textColor = getRoomTextColor(room);

          return (
            <g
              key={room.id}
              className="room-group"
              onClick={() => !isLocked && onRoomSelect?.(room.id)}
              onMouseEnter={() => setHoveredRoom(room.id)}
              onMouseLeave={() => setHoveredRoom(null)}
              style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
              role="button"
              aria-label={`${room.name} - ${isBooked ? (isSharedRoom ? 'Has bookings' : 'Booked') : 'Available'}`}
              tabIndex={0}
            >
              <rect
                x={f.x} y={f.y} width={f.w} height={f.h}
                fill={getRoomColor(room)}
                opacity={getRoomOpacity(room)}
                stroke={selectedRoom === room.id ? '#fff' : (hasFloorplanImage ? (mapTheme.border || getRoomColor(room)) : '#333')}
                strokeWidth={selectedRoom === room.id ? 3 : (hasFloorplanImage ? (mapTheme.borderWidth ?? 2.5) : 1)}
                strokeOpacity={hasFloorplanImage ? 0.9 : 1}
                rx={isCubicle ? 4 : 6}
              />
              <text
                x={f.x + f.w / 2}
                y={f.y + ((isBooked && isCubicle)
                  ? f.h * 0.32                                  // booked cube: ID near top, name fills the rest
                  : f.h / 2 + (sublabel ? -6 * labelScale : 0))}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={fitFont(label, f.w, fontSize, labelScale)}
                fontWeight="600"
                fill={textColor}
                pointerEvents="none"
              >
                {label}
              </text>
              {sublabel && (
                <text
                  x={f.x + f.w / 2}
                  y={f.y + f.h / 2 + 10 * labelScale}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={fitFont(sublabel, f.w, 10, labelScale)}
                  fill={textColor}
                  opacity="0.85"
                  pointerEvents="none"
                >
                  {sublabel}
                </text>
              )}
              {/* Cubicles/desks always show who booked, even when small (h < 70).
                  Cube/desk names wrap onto multiple lines at spaces so they don't
                  overflow the tile; a single name is never split mid-word. */}
              {isBooked && (isCubicle || !isSmall) && (() => {
                const who = availability[room.id]?.bookedBy || 'Reserved';
                const words = isCubicle ? who.split(/\s+/).filter(Boolean) : [who];
                const longest = words.reduce((a, b) => (b.length > a.length ? b : a), '');
                const fs = fitFont(longest, f.w - 6, 9, labelScale);
                const lh = fs * 1.12;
                const bottomY = f.y + f.h - (isCubicle ? 7 : 14);
                return (
                  <text
                    x={f.x + f.w / 2}
                    textAnchor="middle"
                    fontSize={fs}
                    fill={textColor}
                    opacity="0.9"
                    pointerEvents="none"
                  >
                    {words.map((w, i) => (
                      <tspan
                        key={i}
                        x={f.x + f.w / 2}
                        y={bottomY - (words.length - 1 - i) * lh}
                      >
                        {w}
                      </tspan>
                    ))}
                  </text>
                );
              })()}
            </g>
          );
        })}

        {/* Non-bookable rooms (white fill, thin black border) */}
        {rooms.filter(r => r.bookable === false).map((room) => {
          const f = room.floor;
          const hasAssignee = !!room.assignedTo;
          // Assigned rooms: name on top, title below
          // Utility rooms: just the room name centered
          // Common areas (lounges, break room, open seating): dotted outline,
          // white fill at 80% so the furniture in the drawing shows through
          const isCommon = !!room.commonArea;
          return (
            <g key={room.id} style={{ cursor: 'default' }}>
              <rect
                x={f.x} y={f.y} width={f.w} height={f.h}
                fill={isCommon ? '#fff' : (mapTheme.unbookableFill || '#fff')}
                opacity={isCommon ? 0.8 : 0.95}
                stroke={mapTheme.border || '#111'}
                strokeWidth={1}
                strokeDasharray={isCommon ? '8 6' : undefined}
                rx={6}
              />
              {hasAssignee ? (
                <>
                  <text
                    x={f.x + f.w / 2}
                    y={f.y + f.h / 2 - 8 * labelScale}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={fitFont(room.assignedTo, f.w, 11, labelScale)}
                    fontWeight="600"
                    fill={isCommon ? '#231F20' : (mapTheme.unbookableText || '#333')}
                    pointerEvents="none"
                  >
                    {room.assignedTo}
                  </text>
                  <text
                    x={f.x + f.w / 2}
                    y={f.y + f.h / 2 + 8 * labelScale}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={fitFont(room.mapLabel || room.name, f.w, 9, labelScale)}
                    fill={isCommon ? '#3d3d3d' : (mapTheme.unbookableSub || '#555')}
                    pointerEvents="none"
                  >
                    {room.mapLabel || room.name}
                  </text>
                </>
              ) : (
                (() => {
                  const words = room.wrapLabel ? room.name.split(' ') : [room.name];
                  const sizeText = words.reduce((a, b) => (b.length > a.length ? b : a), '');
                  const fs = room.rotateLabel
                    ? fitFont(room.name, f.h, 14, labelScale)
                    : fitFont(sizeText, f.w, 11, labelScale);
                  const lh = fs * 1.15;
                  return (
                    <text
                      x={f.x + f.w / 2}
                      y={f.y + f.h / 2}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={fs}
                      fontWeight="600"
                      fill={isCommon ? '#231F20' : (mapTheme.unbookableText || '#333')}
                      transform={room.rotateLabel ? `rotate(-90 ${f.x + f.w / 2} ${f.y + f.h / 2})` : undefined}
                      pointerEvents="none"
                    >
                      {words.map((word, i) => (
                        <tspan
                          key={i}
                          x={f.x + f.w / 2}
                          dy={i === 0 ? -((words.length - 1) / 2) * lh : lh}
                        >
                          {word}
                        </tspan>
                      ))}
                    </text>
                  );
                })()
              )}
            </g>
          );
        })}
        {/* Hidden easter egg tap targets */}
        {eggs.map((egg, i) => (
          <rect
            key={`egg-${i}`}
            x={egg.x} y={egg.y} width={egg.w} height={egg.h}
            fill="transparent"
            pointerEvents="all"
            onClick={() => triggerEgg(egg.text)}
          />
        ))}
      </svg>
      </div>
      <button
        type="button"
        className="zoom-toggle-btn"
        onClick={() => setZoomed(z => !z)}
      >
        {zoomed ? 'Exit Zoom' : 'Zoom / Pan Map'}
      </button>

      {eggText && (
        <div
          className="easter-egg-overlay"
          style={{ color: officeConfig.theme?.accent || '#0086C6' }}
        >
          {eggText}
        </div>
      )}

      <div className="floorplan-footnote">* Equipped with ultra-widescreen monitor</div>

      {isKiosk && <GuestWifiButton wifi={officeConfig.theme?.wifi} />}
    </div>
  );
}

function GuestWifiButton({ wifi }) {
  const [showWifi, setShowWifi] = useState(false);
  const network = wifi?.network || 'SSPGUEST';
  const password = wifi?.password || 'sspismovingonup';

  useEffect(() => {
    if (!showWifi) return;
    const timer = setTimeout(() => setShowWifi(false), 10000);
    return () => clearTimeout(timer);
  }, [showWifi]);

  return (
    <>
      <button
        className="guest-wifi-btn"
        onClick={() => setShowWifi(true)}
      >
        Click Here for Guest WiFi
      </button>

      {showWifi && (
        <div className="wifi-overlay" onClick={() => setShowWifi(false)}>
          <div className="wifi-popup" onClick={(e) => e.stopPropagation()}>
            <h3>Guest WiFi</h3>
            <div className="wifi-detail">
              <span className="wifi-label">Network</span>
              <span className="wifi-value">{network}</span>
            </div>
            <div className="wifi-detail">
              <span className="wifi-label">Password</span>
              <span className="wifi-value">{password}</span>
            </div>
            <div className="wifi-countdown">This will close automatically</div>
          </div>
        </div>
      )}
    </>
  );
}
